import axios from 'axios';
import axiosRetry from 'axios-retry';
import { EventEmitter } from 'events';
import whilst from 'async/whilst';
import * as Promise from 'bluebird';
import io from 'socket.io-client';
import parseUsername from '../utils/ParseUsername.js';

axiosRetry(axios, { retries: 3 });

const { VUE_APP_API_SERVER:API_SERVER } = process.env;

const axiosCache = axios.create({
    headers: {
        withCredentials: true,
    },
});

axiosCache.defaults.raxConfig = {
    instance: axiosCache,
};
axios.interceptors.response.use(
    function (response) {
        return response;
    },
    function (error) {
        console.log(error);
        return error;
    }
);

const unsplashConstant = [
    'photo-1524502397800-2eeaad7c3fe5',
    'photo-1523371542221-b2965445c8f3',
    'photo-1526510747491-58f928ec870f',
    'photo-1499552442814-13341b4aed72',
    'photo-1520090651682-088bf846fafe',
    'photo-1495914510314-ba3164b1321f',
    'photo-1467632499275-7a693a761056',
    'photo-1617683423810-ad210c288356',
    'photo-1573496358961-3c82861ab8f4',
    'photo-1523264766116-1e09b3145b84',
    'photo-1541216970279-affbfdd55aa8',
    'photo-1488282396544-0212eea56a21',
    'photo-1529123202249-4f6224196c9b',
    'photo-1644424234835-37c1aed58a60',
    'photo-1644424236371-190b4ec5ff77',
];

class BackendService extends EventEmitter {
    constructor(Vue) {
        super();
        this.axiosCache = axiosCache;
        this.$auth = Vue.prototype.$auth;
        this.$near = Vue.prototype.$near;
        this.$store = Vue.prototype.$store;

        this.unsplash = Array.from(unsplashConstant);
        this.usedUnsplash = this.$store.state.usedUnsplash;

        async function setupIO(self) {
            await whilst(
                function (callback) {
                    callback(null, self.$auth.loading);
                },
                function (callback) {
                    Promise.delay(100).then(callback);
                }
            );
            const token = await self.$auth.getTokenSilently();
            self.io = io(`${API_SERVER}/user`, {
                auth: { token },
                transports: ['websocket'],
                rejectUnauthorized:
                    process.env.NODE_ENV === 'production' ? true : false,
            })
                .on('callMintContract', async (contractArgs) => {
                    self.$store.commit('setHook', {
                        callMintContract: { set: Date.now() },
                    });
                    self.$near.mint(contractArgs);
                })
                .on('connect', () => {
                    self.io.emit('near-worker');
                })
                .on('connect_error', (error) => {
                    error;
                    //console.log(error);
                });
        }
        setupIO(this);
        setInterval(this.getLatestNearListing.bind(this), 60000);
    }
    randomUnusedUnsplash(network, username) {
        if (this.usedUnsplash[`${network}_${username}`])
            return this.usedUnsplash[`${network}_${username}`];
        const randomIndex = Math.floor(Math.random() * this.unsplash.length);
        this.usedUnsplash[`${network}_${username}`] = this.unsplash.splice(
            randomIndex,
            1
        );
        if (this.unsplash.length === 0)
            this.unsplash = Array.from(unsplashConstant); // loop through them again
        this.$store.commit({ type: 'set', usedUnsplash: this.usedUnsplash });
        return this.usedUnsplash[`${network}_${username}`];
    }
    async GET(options) {
        const { url, params } = options;
        return new Promise((resolve, reject) => {
            axiosCache
                .get(url, {
                    params,
                })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    POST(options) {
        const { url, data, params } = options;
        return new Promise((resolve) => {
            axiosCache
                .post(url, data, {
                    params,
                })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    console.log(error);
                    resolve(error);
                });
        });
    }
    async openSupportIssue(params) {
        const { email, subject, message } = params;
        const response = await this.POST({
            url: `${API_SERVER}/v1/support`,
            data: {
                email,
                subject,
                message,
            },
        });
        return response.data;
    }
    async getLatestNearListing() {
        const response = await this.request({
            url: `${API_SERVER}/v1/nft/near/listing/latest`,
            method: 'get',
        });
        this.$store.commit({ type: 'set', lastNearListing: response.data });
        return response.data;
    }
    async createNft(params) {
        let self = this;
        const { fields } = params;
        const response = await this.authenticatedRequest({
            url: `${API_SERVER}/v1/account/nft/create`,
            body: { ...fields },
            method: 'post',
        });
        if (response instanceof Error) throw response;
        const { jobId } = response.data;
        let job;
        await whilst(
            function (callback) {
                self.getJobStatus(jobId, 'premint').then((j) => {
                    if (j instanceof Error) return callback(j);
                    if (j.failedReason) throw new Error(j.failedReason);
                    job = j;
                    job.id = jobId;
                    callback(null, !job.finishedOn);
                });
            },
            function (callback) {
                Promise.delay(10000).then(callback);
            }
        );
        return job;
    }
    async createNftMintStatus(jobId) {
        let mintedNft,
            self = this;
        await whilst(
            function (callback) {
                self.getJobStatus(jobId, 'mint').then((job) => {
                    if (job instanceof Error) return callback(job);
                    if (job.failedReason) {
                        throw new Error(job.failedReason);
                    }
                    mintedNft = job.data;
                    callback(null, !job.finishedOn);
                });
            },
            function (callback) {
                Promise.delay(10000).then(callback);
            }
        );
        return mintedNft;
    }
    async getJobStatus(jobId, queue) {
        try {
            const response = await this.authenticatedRequest({
                url: `${API_SERVER}/v1/account/jobs/${jobId}?queue=${queue}`,
                method: 'get',
            });
            return response.data;
        } catch (error) {
            self.$store.commit({ type: 'set', creationState: {} });
            return error;
        }
    }
    async getNft(id, updateCache = false) {
        const response = await this.request({
            url: `${API_SERVER}/v1/account/nft/${id}`,
            method: 'get',
            params: { nocache: updateCache },
        });
        return response.data;
    }
    async getNFTs(params) {
        const { auth, query } = params;
        const response = auth
            ? await this.authenticatedRequest({
                  url: `${API_SERVER}/v1/account/nfts`,
                  method: 'get',
                  params: query,
              })
            : await this.request({
                  url: `${API_SERVER}/v1/nft`,
                  method: 'get',
                  params: query,
              });
        return response.data.map((nft) => {
            if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
                nft.photos = nft.photos.map((photo) => {
                    photo.url = photo.url.replace('.webp', '.jpeg');
                    return photo;
                });
            }
            return {
                parsedUsername: parseUsername(nft.creator),
                ...nft,
            };
        });
    }
    async accountWishlists(params = {}) {
        const { wishlist_id } = params;
        const response = await this.authenticatedRequest({
            url: `${API_SERVER}/v1/account/wishlist${wishlist_id ? '/' + wishlist_id : ''}`,
            method: 'get',
        });
        return response.data;
    }
    async sharedWishlists(params = {}) {
        const { ids } = params;
        const response = await this.request({
            url: `${API_SERVER}/v1/wishlist`,
            method: 'get',
            params: ids,
        });
        return response.data;
    }
    async sharedWishlist(params) {
        const { wishlist_id } = params;
        const response = await this.request({
            url: `${API_SERVER}/v1/wishlist/${wishlist_id}`,
            method: 'get',
        });
        return response.data;
    }
    async createWishlist(params) {
        const { fields } = params;
        console.log({fields})
        const response = await this.authenticatedRequest({
            url: `${API_SERVER}/v1/account/wishlist`,
            method: 'post',
            body: { ...fields },
        });
        return response.data;
    }
    async editWishlist(params) {
        const { id, fields } = params;
        const response = await this.authenticatedRequest({
            url: `${API_SERVER}/v1/account/wishlist/${id}`,
            method: 'put',
            body: { ...fields },
        });
        return response.data;
    }
    async request(options) {
        return new Promise((resolve, reject) => {
            const { url, method, body, params } = options;
            let func;
            switch (method) {
                case 'get':
                    func = axiosCache.get;
                    break;
                case 'post':
                    func = axiosCache.post;
                    break;
                case 'head':
                    func = axiosCache.head;
                    break;
                default:
                    func = axiosCache.get;
            }
            const requestOptions = {
                params,
            };
            func(url, method === 'post' ? body : requestOptions, requestOptions)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    async head(options) {
        const { url } = options;
        const response = await this.request({
            url,
            method: 'head',
        });
        response;
    }
    async authenticatedRequest(options) {
        const { url, method, body, params } = options;
        const self = this;
        let func;
        switch (method) {
            case 'get':
                func = axiosCache.get;
                break;
            case 'post':
                func = axiosCache.post;
                break;
            case 'put':
                func = axiosCache.put;
                break;
            case 'patch':
                func = axiosCache.patch;
                break;
            default:
                func = axiosCache.get;
        }
        return new Promise((resolve, reject) => {
            let token;
            function wait() {
                setTimeout(async () => {
                    if (!self.$auth.loading) {
                        try {
                            token = await self.$auth.getTokenSilently();
                        } catch (error) {
                            console.log(error);
                        }
                        const options = {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                            params,
                        };
                        //if (method.match(/put/)) options.headers['Content-Type'] = 'multipart/form-data';
                        func(
                            url,
                            method.match(/post|put|patch/) ? body : options,
                            options
                        )
                            .then((response) => {
                                resolve(response);
                            })
                            .catch((error) => {
                                console.log(error);
                                reject(error);
                            });
                    } else {
                        wait();
                    }
                }, 500);
            }
            wait();
        });
    }
    async authenticatedGETRequest(options) {
        const { url, params } = options;
        const self = this;
        return new Promise((resolve, reject) => {
            let token;
            function wait() {
                setTimeout(async () => {
                    if (!self.$auth.loading) {
                        try {
                            token = await self.$auth.getTokenSilently();
                        } catch (error) {
                            console.log(error);
                        }
                        axiosCache
                            .get(url, {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                },
                                params,
                            })
                            .then((response) => {
                                resolve(response);
                            })
                            .catch((error) => {
                                console.log(error);
                                reject(error);
                            });
                    } else {
                        wait();
                    }
                }, 500);
            }
            wait();
        });
    }
    async buildInfo() {
        return new Promise((resolve, reject) => {
            axiosCache
                .get(`${window.origin}/build-info.json`, {
                    headers: {
                        'Cache-Control': 'no-store, max-age=0',
                    },
                })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    console.log(error);
                    reject(error);
                });
        });
    }
    async getAvailableUsernames(count) {
        const response = await this.request({
            url: `${API_SERVER}/v1/users/available`,
            params: { count },
        });
        return response.data;
    }
    async socialNetworkProfilePhoto(
        network,
        username,
        filepond_social_profile_photo
    ) {
        try {
            const response = filepond_social_profile_photo
                ? await this.authenticatedRequest({
                      url: `${API_SERVER}/v1/social/${network}/${username}`,
                      method: 'post',
                      body: { filepond_social_profile_photo },
                  })
                : await this.request({
                      url: `${API_SERVER}/v1/social/${network}/${username}`,
                  });
            return response.data;
        } catch (error) {
            if (!(error.response && error.response.status === 404))
                console.log(error.message);
            return {
                photo: {
                    placeholder: true,
                    https: `https://images.unsplash.com/${this.randomUnusedUnsplash(
                        network,
                        username
                    )}?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=300&q=90`,
                },
            };
        }
    }
    async getCreatorAliases(creatorId, format = true) {
        function getUsersSocialHomeUrl(network, username) {
            switch (network) {
                case 'tiktok':
                    return `https://tiktok.com/@${username}`;
                case 'titscore':
                    return `https://titscore.com/profile/${username}`;
                case 'twitter':
                    return `https://twitter.com/@${username}`;
                case 'facebook':
                    return `https://facebook.com/${username}`;
                default:
                    return `https://${network}.com/${username}`;
            }
        }
        try {
            const response = await this.request({
                url: `${API_SERVER}/v1/users/social/aliases`,
                params: { id: creatorId },
            });
            if (!response.data) return;
            if (format) {
                const htmlFormatted = response.data.map((alias) => {
                    const network = Object.keys(alias)[0];
                    const username = Object.values(alias)[0];
                    return `<a target="_blank" rel="nofollow" href="${getUsersSocialHomeUrl(
                        network,
                        username
                    )}">${username}</a><span class="ml-1 social-label">${network}</span>`;
                });
                return htmlFormatted;
            }
            return response.data;
        } catch (error) {
            console.log(error);
            return; // no return here
        }
    }
    async walletRecovery() {
        const response = await this.authenticatedRequest({
            url: `${API_SERVER}/v1/account/wallet/recovery`,
            method: 'get',
        });
        this.$root.$emit('recoveryRequestSent');
        return response.data;
    }
}

export default (auth) => new BackendService(auth);
